import { getRequestIP } from 'h3'
import { redirect } from '@@/shared-components/composables/redirects'
import type { RouteLocationNormalized } from 'vue-router'

export default defineNuxtPlugin({
  name: 'redirect',
  setup: () => {
    let originIp: string | undefined
    const nuxtApp = useNuxtApp()

    const isSSR = useState('isSSR', () => import.meta.server)
    let inited = !isSSR.value
    onNuxtReady(() => {
      inited = true
    })

    const sendRedirectLog = async (path: string, origin: string) => {
      if (import.meta.server) {
        const event = useRequestEvent()
        originIp = getRequestIP(event!, { xForwardedFor: true })
      } else {
        if (!import.meta.dev) {
          try {
            const data = await useCustomFetch<{ ip: string }>({
              url: '/public/ip-info',
              clientOnly: true,
            })
            originIp = data?.ip
          } catch (e) {
            console.error('Chyba při získávání IP adresy:', e)
          }
        }
      }
      // Na Error page se middleware spouští znovu, proto kontrola import.meta.server && useError().value
      // https://github.com/nuxt/nuxt/issues/25084
      if ((import.meta.server && useError().value) || inited) {
        const currentURL = new URL(path, origin)

        return await useCustomFetch<{
          data: {
            redirect: boolean
            _link: {
              identifier: number
              slug: string
              type: {
                name: string
                value: LinkTypes
              }
              externalLink: string
            }
            else: {
              identifier: number
              slug: string
              type: {
                name: string
                value: LinkTypes
              }
              externalLink: string
            }[]
          }
        }>({
          url: '/public/urls',
          options: {
            method: 'POST',
            body: {
              url: currentURL.href,
              type: 404,
              originIp: originIp === '::1' ? '127.0.0.1' : originIp,
            },
          },
          clientOnly: true,
        })
      }
    }

    nuxtApp.hook('vue:error', (error) => {
      if (
        typeof error === 'object' &&
        error &&
        'statusCode' in error &&
        error?.statusCode === 404
      ) {
        const url = useRequestURL()
        const route = useRoute()
        sendRedirectLog(route.fullPath, url.origin)
      }
    })
    addRouteMiddleware(
      'redirect',
      async (to) => {
        return await nuxtApp.runWithContext(async () => {
          if (
            (!to.name ||
              (to.name &&
                to.meta.validate &&
                to.meta.validate(to as RouteLocationNormalized) === false)) &&
            !to.fullPath.includes('nuxt_error') &&
            !to.fullPath.includes('undefined') &&
            !to.fullPath.includes('.png') &&
            !to.fullPath.includes('.js') &&
            !to.fullPath.includes('__profiler') &&
            ((import.meta.server && useError().value) || inited)
          ) {
            const url = useRequestURL()
            const res = await sendRedirectLog(to.fullPath, url.origin)
            return await nuxtApp.runWithContext(async () => {
              if (res?.data.redirect) {
                const redirectTo = redirect(
                  res.data?._link?.type?.value,
                  {
                    id: res.data?._link.identifier,
                    slug: res.data?._link.slug,
                  },
                  res.data._link?.externalLink
                )

                if (redirectTo !== to.path) {
                  return appNavigateTo(redirectTo, {
                    external: !!res.data._link?.externalLink,
                  })
                }
              }
            })
          }
        })
      },
      { global: true }
    )
  },
})
